<template>
  <div>
    <v-container
      bg
      fill-height
      grid-list-md
      text-xs-center
      class="pt-5"
      v-if="groups.length === 0"
    >
      <v-row no-gutters align-center class="pt-5">
        <v-col cols="6" sm="12">
          <v-card class="justify-center">
            <v-row>
              <v-card-text class="text-center pt-4">
                <h4>You still have no working groups</h4>
                <div style="white-space: pre-wrap">
                  {{ helpPresentationText }}
                </div>
                <exo-help-dialog
                  width="1000"
                  margin="m-0 ml-1 justify-center"
                  title="Groups"
                >
                  <h5>Presentation</h5>
                  <p style="white-space: pre-wrap">
                    {{ helpPresentationText }}
                  </p>
                  <h5>Technical notes</h5>
                  <p style="white-space: pre-wrap">
                    {{ helpTechnicalNotesText }}
                  </p>
                </exo-help-dialog>
              </v-card-text>
            </v-row>

            <v-divider class="p-0 m-0"></v-divider>

            <v-card-actions class="justify-center p-4">
              <v-btn
                class="white--text"
                color="purple darken-2"
                @click="createGroupDialog = true"
              >
                Create a new group
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <v-col v-else>
      <v-row>
        <v-col cols="3" v-for="group in groups" :key="group.id">
          <group-card :group="group"></group-card>
        </v-col>
        <v-col cols="3">
          <v-card
            @click="createGroupDialog = true"
            class="blue"
            height="100%"
            outlined
            flat
            tile
            min-height="250"
          >
            <v-row justify="center" align="center" class="fill-height">
              <v-col class="ml-5" cols="3">
                <v-icon size="75" class="white--text">add</v-icon>
              </v-col>
              <v-col class="ml-5" cols="7">
                <v-row>
                  <div class="white--text mt-4 body-1">Create Group</div>
                  <exo-help-dialog
                    :color="'white--text'"
                    width="1000"
                    margin="mt-1 ml-0"
                    title="Groups"
                  >
                    <h5>Presentation</h5>
                    <p style="white-space: pre-wrap">
                      {{ helpPresentationText }}
                    </p>
                    <h5>Technical notes</h5>
                    <p style="white-space: pre-wrap">
                      {{ helpTechnicalNotesText }}
                    </p>
                  </exo-help-dialog>
                </v-row>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
    <v-dialog max-width="650" persistent v-model="createGroupDialog">
      <v-card>
        <v-container class="pt-0">
          <v-card-title>
            <span class="headline">Create Group</span>
            <v-spacer></v-spacer>
            <v-btn icon small class="m-0" @click="createGroupDialog = false">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider class="mt-0 mb-3"></v-divider>
          <v-card-text>
            <v-form v-model="createGroupFormValid" ref="groupform">
              <v-text-field
                v-model="groupName"
                :rules="groupNameRules"
                label="Group name"
              ></v-text-field>
              <p class="body-2 m-0 grey--text">SHARE WITH</p>
              <div
                class="d-flex"
                v-for="(user, index) in shareWith"
                :key="index"
              >
                <v-col cols="7" mr-2>
                  <v-text-field
                    v-model="user.email"
                    label="E-mail"
                  ></v-text-field>
                </v-col>
                <v-col cols="4">
                  <v-select
                    :items="authOptions"
                    v-model="user.auth"
                    :rules="authRules"
                    label="User authorization"
                    :hint="
                      user.auth === 'Admin'
                        ? 'User can import and export data'
                        : 'User can only import data'
                    "
                  ></v-select>
                </v-col>
                <v-col cols="1" class="pt-2">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        class="grey--text darken-4"
                        v-on:click="removeUser(index)"
                      >
                        <v-icon size="29">delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Remove user</span>
                  </v-tooltip>
                </v-col>
              </div>
            </v-form>
            <div class="text-right">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn class="primary" v-on:click="addUserToGroup()">
                    <v-icon class="mr-1" size="29">add</v-icon> Add user
                  </v-btn>
                </template>
                <span>Add user</span>
              </v-tooltip>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="primary"
              @click="createGroup()"
              :disabled="!createGroupFormValid"
              >Create Group</v-btn
            >
            <v-btn color="primary" @click="resetForm()" outlined>Cancel</v-btn>
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import groupsComponents from '../components/Groups/index'
import errorMixin from '../mixins/logHandler.js'

Vue.use(groupsComponents)

export default {
  name: 'Groups',

  mixins: [errorMixin],

  mounted() {
    this.$root.$emit('set-current-mission', null)
  },

  computed: {
    ...mapGetters({ currentUser: 'currentUser', groups: 'groups' }),

    helpPresentationText() {
      return (
        'Groups are used to share missions between users.\n' +
        'They can also be used to share use cases with the support team for investigations.\n' +
        'A mission can be shared to a group by clicking on the "Share" button in the mission table of the main window.'
      )
    },
    helpTechnicalNotesText() {
      return (
        'When sharing a mission, a copy is made including a copy of all used objects.' +
        ' ' +
        'A shared mission is independent from all other missions, which means that the used objects are only referenced in this very mission.' +
        ' ' +
        'Changing the original mission will have no impact on the shared mission.\n' +
        'Note that mission results are not shared. A new computation will be in order when importing a shared mission.\n' +
        'Shared missions should be manually deleted directly from the group when there are no longer needed.'
      )
    }
  },

  data() {
    return {
      emailRegex:
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      authOptions: ['Admin', 'View'],
      createGroupDialog: false,
      createGroupFormValid: false,
      groupName: '',
      groupNameRules: [(v) => !!v || 'Group name is required'],
      shareWith: [
        {
          email: '',
          auth: ''
        }
      ],
      emailRules: [
        (v) => this.emailRegex.test(v) || 'E-mail must be valid',
        (v) => !!v || 'E-mail is required'
      ],
      authRules: [(v) => !!v || 'Authorization is required']
    }
  },

  methods: {
    async createGroup() {
      this.$http
        .post('/groups/' + this.currentUser.id + '/group_create', {
          groupName: this.groupName,
          usersList: this.shareWith
        })
        .then((response) => {
          switch (response.status) {
            case 206:
              this.displayMessage(response.data, '', 'notification', 'warning')
              break
            default:
              this.displayMessage(
                'Group ' + this.groupName + ' was created successfully.',
                '',
                'notification',
                'success'
              )
              break
          }
          this.groupName = ''
          this.resetForm()
          this.$root.$emit('update-groups-list')
        })
        .catch((e) =>
          this.displayMessage(
            'Failed to create group ' + this.groupName + '.',
            e,
            'notification',
            'error'
          )
        )
    },

    resetForm() {
      this.createGroupDialog = false
      this.$refs.groupform.reset()
      this.$refs.groupform.resetValidation()
    },

    addUserToGroup() {
      this.shareWith.push({
        email: '',
        auth: ''
      })
    },

    removeUser(index) {
      this.shareWith.splice(index, 1)
    }
  }
}
</script>

<style scoped>
.align-center {
  align-items: center;
  justify-content: center;
}
</style>
